import { Form } from "antd";
import { RefundResult } from "api/responses";
import { usePartialRefundSubmission } from "./usePartialRefundSubmission";
import { useSubmitPartialRefunds } from "./useSubmitPartialRefunds";
import { useTransactionRefund } from "api/clients/useTransactionRefund";
import type { ManualPartialRefundRequest } from "api/requests";
import type { RefundModalEvent } from "./refundModal.events";

function usePartialRefund(patientId: number, transactionId: number, dispatch: React.Dispatch<RefundModalEvent>) {
    const [partialRefundForm] = Form.useForm<ManualPartialRefundRequest>();

    const { isPending, submit: mutate } = usePartialRefundSubmission(patientId, partialRefundForm, dispatch, {
        onError: () => dispatch({ type: "refund-result", pending: false, success: false, result: RefundResult.None }),
        onSuccess: (data, variables) => dispatch({ type: "refund-result", ...data, amount: variables.amount }),
    });

    const submit = async () => await mutate(transactionId);

    return { partialRefundForm, isPending, submit };
}

function useFullRefund(patientId: number, transactionId: number, dispatch: React.Dispatch<RefundModalEvent>) {
    const { isPending, mutate } = useTransactionRefund(patientId, {
        onError: () => dispatch({ type: "refund-result", pending: false, success: false, result: RefundResult.None }),
        onSuccess: (data) => dispatch({ type: "refund-result", ...data }),
    });

    const submit = () => {
        dispatch({ type: "confirm-refund" });
        mutate(transactionId);
    };

    return { isPending, submit };
}

/** Perform either partial or full refund submission depending on which is available. */
export function useRefundSubmission(
    patientId: number,
    transactionId: number,
    dispatch: React.Dispatch<RefundModalEvent>,
) {
    const usePartialRefunds = useSubmitPartialRefunds();

    const {
        partialRefundForm,
        isPending: partialPending,
        submit: partialSubmit,
    } = usePartialRefund(patientId, transactionId, dispatch);

    const { isPending: fullPending, submit: fullSubmit } = useFullRefund(patientId, transactionId, dispatch);

    const submit = async () => {
        if (usePartialRefunds) {
            await partialSubmit();
        } else {
            fullSubmit();
        }
    };

    return {
        partialRefundForm,
        pending: usePartialRefunds ? partialPending : fullPending,
        submit,
    };
}
